import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  TrendingUp,
  Building2,
  Shield,
  Brain,
  Sparkles,
  ArrowRight,
  Check,
  X,
  RefreshCw,
  Trophy,
  Zap,
  Target,
  ChevronRight,
  Play,
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './Quiz.css';

const DIFFICULTY_META = {
  easy:   { label: 'Easy',   color: '#10b981' },
  medium: { label: 'Medium', color: '#f59e0b' },
  hard:   { label: 'Hard',   color: '#ef4444' },
};

const ICONS = {
  basics: BookOpen,
  technical: TrendingUp,
  fundamental: Building2,
  risk: Shield,
  psychology: Brain,
};

const STAGE = { LANDING: 'landing', ANSWERING: 'answering', RESULT: 'result' };

const normalizeOptions = (opts) => {
  if (Array.isArray(opts)) return opts;
  if (typeof opts === 'string') {
    try {
      const parsed = JSON.parse(opts);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
  }
  return [];
};

const normalizeQuestion = (q) => ({
  ...q,
  options: normalizeOptions(q.options),
  correctAnswer: Number(q.correctAnswer) || 0,
});

const Quiz = () => {
  const { isAuthenticated } = useAuth();

  const [stage, setStage] = useState(STAGE.LANDING);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [questionCount, setQuestionCount] = useState(10);
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]); // {questionId, chosen, correct}
  const [selected, setSelected] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/quiz/categories');
        if (res.data.success) setCategories(res.data.data.categories || []);
      } catch (e) {
        console.error('Load quiz categories failed:', e);
      }
    })();
  }, []);

  const totalQuestions = useMemo(
    () => categories.reduce((sum, c) => sum + (c.count || 0), 0),
    [categories]
  );

  const startQuiz = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await api.get('/quiz/questions', {
        params: { category: selectedCategory, difficulty: selectedDifficulty, count: questionCount }
      });
      if (res.data.success && res.data.data.questions.length > 0) {
        setQuestions(res.data.data.questions.map(normalizeQuestion));
        setCurrent(0);
        setAnswers([]);
        setSelected(null);
        setShowFeedback(false);
        setStage(STAGE.ANSWERING);
      } else {
        setError('No questions match your filters. Try a different category or difficulty.');
      }
    } catch (e) {
      setError('Failed to load questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (idx) => {
    if (showFeedback) return;
    setSelected(idx);
  };

  const handleConfirm = () => {
    if (selected == null) return;
    const q = questions[current];
    const isCorrect = selected === q.correctAnswer;
    setAnswers((prev) => [...prev, { question: q, chosen: selected, correct: isCorrect }]);
    setShowFeedback(true);
  };

  const handleNext = () => {
    if (current + 1 >= questions.length) {
      setStage(STAGE.RESULT);
    } else {
      setCurrent(current + 1);
      setSelected(null);
      setShowFeedback(false);
    }
  };

  const restart = () => {
    setStage(STAGE.LANDING);
    setQuestions([]);
    setAnswers([]);
    setCurrent(0);
    setSelected(null);
    setShowFeedback(false);
  };

  const correctCount = answers.filter((a) => a.correct).length;
  const totalCount = answers.length;
  const percent = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;

  const scoreTier = useMemo(() => {
    if (percent >= 90) return { tag: 'Master Trader',   color: '#f59e0b', desc: 'Outstanding! Your market knowledge is rock-solid.' };
    if (percent >= 70) return { tag: 'Advanced',        color: '#10b981', desc: 'Great foundation. Keep sharpening your edge.' };
    if (percent >= 50) return { tag: 'Intermediate',    color: '#3b82f6', desc: 'Solid start — plenty of room to grow.' };
    return                { tag: 'Beginner',           color: '#ef4444', desc: 'Time to strengthen the fundamentals.' };
  }, [percent]);

  // ============ RENDER: LANDING ============
  if (stage === STAGE.LANDING) {
    return (
      <div className="quiz-page premium-page">
        <div className="quiz-page__bg" aria-hidden="true">
          <div className="quiz-page__bg-blob quiz-page__bg-blob--a" />
          <div className="quiz-page__bg-blob quiz-page__bg-blob--b" />
        </div>

        <div className="quiz-container">
          <motion.header
            className="quiz-hero"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="quiz-hero__badge">
              <Sparkles size={14} />
              <span>Stock Knowledge Challenge</span>
            </div>
            <h1 className="quiz-hero__title">
              Stock Knowledge <span className="quiz-hero__title-accent">Challenge</span>
            </h1>
            <p className="quiz-hero__desc">
              Curated must-know concepts for every trader. See where you really stand.<br />
              No sign-up required — <strong>{totalQuestions}</strong> questions ready for you.
            </p>

            <div className="quiz-hero__stats">
              <div className="quiz-hero__stat">
                <Trophy size={18} />
                <span>{totalQuestions} Questions</span>
              </div>
              <div className="quiz-hero__stat">
                <Target size={18} />
                <span>{categories.length} Categories</span>
              </div>
              <div className="quiz-hero__stat">
                <Zap size={18} />
                <span>Randomized</span>
              </div>
            </div>
          </motion.header>

          <motion.section
            className="quiz-config"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="quiz-config__group">
              <label className="quiz-config__label">Choose Category</label>
              <div className="quiz-chips">
                <button
                  type="button"
                  className={`quiz-chip ${selectedCategory === 'all' ? 'quiz-chip--active' : ''}`}
                  onClick={() => setSelectedCategory('all')}
                >
                  <Sparkles size={14} />
                  <span>All</span>
                  <span className="quiz-chip__badge">{totalQuestions}</span>
                </button>
                {categories.map((c) => {
                  const Icon = ICONS[c.key] || BookOpen;
                  const active = selectedCategory === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      className={`quiz-chip ${active ? 'quiz-chip--active' : ''}`}
                      onClick={() => setSelectedCategory(c.key)}
                      style={active ? { borderColor: c.color, color: c.color } : undefined}
                    >
                      <Icon size={14} />
                      <span>{c.label}</span>
                      <span className="quiz-chip__badge">{c.count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="quiz-config__group">
              <label className="quiz-config__label">Choose Difficulty</label>
              <div className="quiz-chips">
                <button
                  type="button"
                  className={`quiz-chip ${selectedDifficulty === 'all' ? 'quiz-chip--active' : ''}`}
                  onClick={() => setSelectedDifficulty('all')}
                >
                  Any Level
                </button>
                {Object.entries(DIFFICULTY_META).map(([key, meta]) => {
                  const active = selectedDifficulty === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`quiz-chip ${active ? 'quiz-chip--active' : ''}`}
                      onClick={() => setSelectedDifficulty(key)}
                      style={active ? { borderColor: meta.color, color: meta.color } : undefined}
                    >
                      <span className="quiz-chip__dot" style={{ background: meta.color }} />
                      <span>{meta.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="quiz-config__group">
              <label className="quiz-config__label">Question Count: {questionCount}</label>
              <input
                type="range"
                min="5"
                max="20"
                step="1"
                value={questionCount}
                onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                className="quiz-slider"
              />
              <div className="quiz-slider__marks">
                <span>5</span><span>10</span><span>15</span><span>20</span>
              </div>
            </div>

            {error && (
              <div className="quiz-error">
                <X size={16} />
                <span>{error}</span>
              </div>
            )}

            <button
              type="button"
              className="quiz-cta"
              onClick={startQuiz}
              disabled={loading || totalQuestions === 0}
            >
              <Play size={18} />
              <span>{loading ? 'Loading...' : 'Start Challenge'}</span>
              <ArrowRight size={18} />
            </button>
          </motion.section>
        </div>
      </div>
    );
  }

  // ============ RENDER: ANSWERING ============
  if (stage === STAGE.ANSWERING) {
    const q = questions[current];
    const progress = ((current + (showFeedback ? 1 : 0)) / questions.length) * 100;

    return (
      <div className="quiz-page premium-page">
        <div className="quiz-page__bg" aria-hidden="true">
          <div className="quiz-page__bg-blob quiz-page__bg-blob--a" />
        </div>

        <div className="quiz-container quiz-container--narrow">
          <div className="quiz-progress">
            <div className="quiz-progress__info">
              <span className="quiz-progress__step">Question {current + 1} / {questions.length}</span>
              <span className="quiz-progress__score">
                <strong>{correctCount}</strong> correct
              </span>
            </div>
            <div className="quiz-progress__bar">
              <div className="quiz-progress__fill" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              className="quiz-card"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.3 }}
            >
              <div className="quiz-card__meta">
                <span
                  className="quiz-card__difficulty"
                  style={{ color: DIFFICULTY_META[q.difficulty]?.color }}
                >
                  <span
                    className="quiz-card__difficulty-dot"
                    style={{ background: DIFFICULTY_META[q.difficulty]?.color }}
                  />
                  {DIFFICULTY_META[q.difficulty]?.label || q.difficulty}
                </span>
              </div>
              <h2 className="quiz-card__question">{q.question}</h2>

              <div className="quiz-options">
                {q.options.map((opt, idx) => {
                  const isSelected = selected === idx;
                  const isCorrect = idx === q.correctAnswer;
                  const showRight = showFeedback && isCorrect;
                  const showWrong = showFeedback && isSelected && !isCorrect;
                  return (
                    <button
                      key={idx}
                      type="button"
                      className={`quiz-option ${isSelected ? 'quiz-option--selected' : ''} ${showRight ? 'quiz-option--correct' : ''} ${showWrong ? 'quiz-option--wrong' : ''}`}
                      onClick={() => handleSelect(idx)}
                      disabled={showFeedback}
                    >
                      <span className="quiz-option__letter">{String.fromCharCode(65 + idx)}</span>
                      <span className="quiz-option__text">{opt}</span>
                      {showRight && <Check size={18} className="quiz-option__icon" />}
                      {showWrong && <X size={18} className="quiz-option__icon" />}
                    </button>
                  );
                })}
              </div>

              {showFeedback && q.explanation && (
                <motion.div
                  className="quiz-explain"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="quiz-explain__label">
                    <Sparkles size={14} />
                    <span>Explanation</span>
                  </div>
                  <p>{q.explanation}</p>
                </motion.div>
              )}

              <div className="quiz-card__actions">
                {!showFeedback ? (
                  <button
                    type="button"
                    className="quiz-cta"
                    onClick={handleConfirm}
                    disabled={selected == null}
                  >
                    <span>Confirm</span>
                    <Check size={18} />
                  </button>
                ) : (
                  <button type="button" className="quiz-cta" onClick={handleNext}>
                    <span>{current + 1 >= questions.length ? 'See Results' : 'Next Question'}</span>
                    <ChevronRight size={18} />
                  </button>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // ============ RENDER: RESULT ============
  return (
    <div className="quiz-page premium-page">
      <div className="quiz-page__bg" aria-hidden="true">
        <div className="quiz-page__bg-blob quiz-page__bg-blob--a" />
        <div className="quiz-page__bg-blob quiz-page__bg-blob--b" />
      </div>

      <div className="quiz-container quiz-container--narrow">
        <motion.div
          className="quiz-result"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <div className="quiz-result__crown" style={{ color: scoreTier.color }}>
            <Trophy size={56} strokeWidth={1.6} />
          </div>

          <div className="quiz-result__score-wrap">
            <div className="quiz-result__score">{percent}<span>%</span></div>
            <div className="quiz-result__ratio">{correctCount} / {totalCount}</div>
          </div>

          <div className="quiz-result__tier" style={{ color: scoreTier.color, borderColor: scoreTier.color }}>
            {scoreTier.tag}
          </div>
          <p className="quiz-result__desc">{scoreTier.desc}</p>

          {/* CTA — guide unauthenticated users to register */}
          {!isAuthenticated && (
            <div className="quiz-result__cta-card">
              <div className="quiz-result__cta-eyebrow">
                <Sparkles size={13} />
                <span>Unlock More Content</span>
              </div>
              <h3>Want to level up?</h3>
              <p>Create a free account to access advanced video courses, in-depth research reports, and VIP trade signals.</p>
              <div className="quiz-result__cta-btns">
                <Link to="/register" className="quiz-cta quiz-cta--sm">
                  <span>Sign Up Free</span>
                  <ArrowRight size={16} />
                </Link>
                <Link to="/videos" className="quiz-cta quiz-cta--ghost quiz-cta--sm">
                  <span>Watch Videos</span>
                </Link>
              </div>
            </div>
          )}

          {answers.filter((a) => !a.correct).length > 0 && (
            <div className="quiz-review">
              <h4 className="quiz-review__title">
                <X size={16} /> Review Incorrect Answers ({answers.filter((a) => !a.correct).length})
              </h4>
              <div className="quiz-review__list">
                {answers.map((a, i) =>
                  a.correct ? null : (
                    <div key={i} className="quiz-review__item">
                      <div className="quiz-review__q">{i + 1}. {a.question.question}</div>
                      <div className="quiz-review__row quiz-review__row--wrong">
                        <span>Your answer:</span>{a.question.options[a.chosen]}
                      </div>
                      <div className="quiz-review__row quiz-review__row--right">
                        <span>Correct:</span>{a.question.options[a.question.correctAnswer]}
                      </div>
                      {a.question.explanation && (
                        <div className="quiz-review__explain">{a.question.explanation}</div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          <div className="quiz-result__actions">
            <button type="button" className="quiz-cta" onClick={startQuiz}>
              <RefreshCw size={18} />
              <span>Play Again</span>
            </button>
            <button type="button" className="quiz-cta quiz-cta--ghost" onClick={restart}>
              <span>Change Category</span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Quiz;
